/**
 * Data hook for searching buyers when creating a chat
 * Follows GraphQL separation pattern - handles GraphQL operations and maps to domain types
 */

import type { BuyerSelectOption } from '@choco-admin/types';
import { useCallback, useMemo } from 'react';

import { useSearchBuyersForChatLazyQuery } from '../../../generated';
import { usePuppyMode } from '../../../hooks';
import { mapToBuyerSelectOption } from '../utils/createChatMapper';

export interface UseSearchBuyersForChatConfig {
  captureException?: (error: Error) => void;
}

export interface UseSearchBuyersForChatResponse {
  buyers: BuyerSelectOption[];
  isLoading: boolean;
  error: Error | null;
  searchBuyers: (searchTerm: string) => Promise<void>;
  clearResults: () => void;
}

export const useSearchBuyersForChat = (
  config: UseSearchBuyersForChatConfig = {}
): UseSearchBuyersForChatResponse => {
  const { captureException } = config;
  const { puppyFilter } = usePuppyMode();

  const [executeSearch, { data, loading, error }] =
    useSearchBuyersForChatLazyQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });

  const searchBuyers = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) return;

      try {
        const filter: any = {};

        // Apply puppy filter if set
        if (puppyFilter !== undefined) {
          filter.buyer = {
            isPuppy: puppyFilter,
          };
        }

        await executeSearch({
          variables: {
            searchTerm: searchTerm.trim(),
            size: 20,
            filter: Object.keys(filter).length > 0 ? filter : undefined,
          },
        });
      } catch (err) {
        const buyersError = err as Error;
        captureException?.(buyersError);
        throw buyersError;
      }
    },
    [executeSearch, puppyFilter, captureException]
  );

  const clearResults = useCallback(() => {
    // Clear results by executing a search with empty term
    executeSearch({
      variables: {
        searchTerm: '',
        size: 0,
      },
    });
  }, [executeSearch]);

  const buyers = useMemo(() => {
    const searchResult = data?.search;

    if (!searchResult?.hits) {
      return [];
    }

    return (
      searchResult.hits
        ?.filter((hit: any) => hit.entity?.__typename === 'Buyer')
        .map((hit: any) => mapToBuyerSelectOption(hit.entity)) || []
    );
  }, [data]);

  return {
    buyers,
    isLoading: loading,
    error: error || null,
    searchBuyers,
    clearResults,
  };
};
